import { useEffect, useState } from "react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

const ROLE_DEFAULTS: Record<string, string[]> = {
  owner: ["clientes", "demandas", "calendario", "ia", "aprovacoes", "campanhas", "relatorios", "financeiro_ver", "financeiro_editar", "equipe", "integracoes"],
  admin: ["clientes", "demandas", "calendario", "ia", "aprovacoes", "campanhas", "relatorios", "financeiro_ver", "equipe", "integracoes"],
  member: ["clientes", "demandas", "calendario", "ia", "campanhas", "relatorios"],
  viewer: ["calendario", "relatorios"],
  client: ["calendario", "aprovacoes", "relatorios"],
};

export function usePermissions() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentWorkspace?.id || !user?.id) {
      setOverrides({});
      setLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchOverrides = async () => {
      setLoading(true);

      const { data, error } = await supabase
        .from("workspace_member_permissions")
        .select("permission, granted")
        .eq("workspace_id", currentWorkspace.id)
        .eq("user_id", user.id)
        .order("permission", { ascending: true });

      if (isCancelled) return;

      if (error) {
        setOverrides({});
        setLoading(false);
        return;
      }

      const map: Record<string, boolean> = {};
      (data as Array<
        Pick<Database["public"]["Tables"]["workspace_member_permissions"]["Row"], "permission" | "granted">
      >).forEach((row) => {
        map[row.permission] = row.granted;
      });
      setOverrides(map);
      setLoading(false);
    };

    void fetchOverrides();

    return () => {
      isCancelled = true;
    };
  }, [currentWorkspace?.id, user?.id]);

  const role = currentWorkspace?.role || "viewer";
  const defaults = new Set(ROLE_DEFAULTS[role] || []);

  function hasPermission(permission: string): boolean {
    if (role === "owner") return true;
    if (permission === "admin") return role === "admin";
    if (permission in overrides) return overrides[permission];
    return defaults.has(permission);
  }

  function canManageTeam(): boolean {
    return role === "owner" || role === "admin";
  }

  return { hasPermission, canManageTeam, loading, role };
}
